/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { View } from 'ol';
import type { Extent } from 'ol/extent';
import { transformExtent } from 'ol/proj';
import { useRef, useEffect } from 'react';
import { WMJSMAP_LONLAT_EPSGCODE } from '@opengeoweb/webmap';
import type { WMLayer } from '@opengeoweb/webmap';
import { useGetWMLayerInstance } from './useHooksGetCapabilities';
import type { OpenLayersLayerProps } from '../components/OpenLayers/layers';
import { initializeOpenLayersProjections } from '../components/OpenLayers/utils/projections';

initializeOpenLayersProjections();

export const getLayerExtentForProjection = (
  wmLayer: WMLayer,
  projectionCode: string,
): Extent | undefined => {
  const crs = wmLayer.getCRSByName(projectionCode);
  if (crs?.bbox) {
    return crs.bbox.getExtent();
  }
  const geo = wmLayer.geographicBoundingBox;
  if (geo) {
    return transformExtent(
      [
        parseFloat(geo.west),
        parseFloat(geo.south),
        parseFloat(geo.east),
        parseFloat(geo.north),
      ],
      WMJSMAP_LONLAT_EPSGCODE,
      projectionCode,
    );
  }
  return undefined;
};

export const useViewFromLayer = (
  layerProps: OpenLayersLayerProps | undefined,
  baseView = {
    projection: 'EPSG:3857',
    zoom: 1,
    center: [0, 0],
  },
): View => {
  const viewRef = useRef<View | null>(null);

  if (viewRef.current === null) {
    try {
      viewRef.current = new View(baseView);
    } catch (_e) {
      viewRef.current = new View({
        projection: 'EPSG:3857',
        zoom: 1,
        center: [0, 0],
      });
      console.warn('Projection not supported');
    }
  }

  const { wmLayer } = useGetWMLayerInstance(layerProps);

  useEffect(() => {
    if (!wmLayer) {
      return;
    }

    const extent = getLayerExtentForProjection(
      wmLayer,
      viewRef.current!.getProjection().getCode(),
    );
    if (!extent) {
      return;
    }
    viewRef.current!.fit(extent);
  }, [wmLayer]);
  return viewRef.current!;
};
